import { endpoints as autogenEnhancements } from '~/api/sylvanus/enhancements.autogen';
import { Tags } from '~/api/sylvanus/tags.autogen';

import { createQueryFn } from '../utils';
import { adminRepublishModel } from './operations/adminRepublishModel';
import { checkLibraryModelConfig } from './operations/checkLibraryModelConfig';
import { checkPromptModelConfig } from './operations/checkPromptModelConfig';
import { createPromptModelTestCase } from './operations/createPromptModelTestCase';
import { createPromptModelVersion } from './operations/createPromptModelVersion';
import { deleteLibraryModel } from './operations/deleteLibraryModel';
import { deletePromptModel } from './operations/deletePromptModel';
import { DEPRECATED_getPromptModelVersion } from './operations/DEPRECATED_getPromptModelVersion';
import { executePromptModelTestCases } from './operations/executePromptModelTestCases';
import { getDocsInScope } from './operations/getDocsInScope';
import { getLibraryModel } from './operations/getLibraryModel';
import { getLibraryModels } from './operations/getLibraryModels';
import { getLibraryModelsPublishStatus } from './operations/getLibraryModelsPublishStatus';
import { getModelFailedDocs } from './operations/getModelFailedDocs';
import { getModelResult } from './operations/getModelResult';
import { getModelsByDocumentId } from './operations/getModelsByDocumentId';
import { getModelsQuotaSummary } from './operations/getModelsQuotaSummary';
import { getModelSummary } from './operations/getModelSummary';
import { getPromptModel } from './operations/getPromptModel';
import { getPromptModelFieldIds } from './operations/getPromptModelFieldIds';
import { getPromptModelOptimizeResults } from './operations/getPromptModelOptimizeResults';
import { getPromptModelOptimizeSummary } from './operations/getPromptModelOptimizeSummary';
import { getPromptModelOptimizeTestCases } from './operations/getPromptModelOptimizeTestCases';
import { getPromptModelProgress } from './operations/getPromptModelProgress';
import { getPromptModelPublish } from './operations/getPromptModelPublish';
import { getPromptModelResults } from './operations/getPromptModelResults';
import { getPromptModelTargetEntities } from './operations/getPromptModelTargetEntities';
import { getPromptModelTestCases } from './operations/getPromptModelTestCases';
import { getPromptModelVersion } from './operations/getPromptModelVersion';
import { getPromptModelWorkflowPrediction } from './operations/getPromptModelWorkflowPrediction';
import { getPromptModelWorkflowPredictionByDocument } from './operations/getPromptModelWorkflowPredictionByDocument';
import { getPromptModelWorkflowPredictions } from './operations/getPromptModelWorkflowPredictions';
import { getPublishedFieldPromptModelTargetEntities } from './operations/getPublishedFieldPromptModelTargetEntities';
import { optimizePromptModel } from './operations/optimizePromptModel';
import { publishLibraryModels } from './operations/publishLibraryModels';
import { publishPromptModel } from './operations/publishPromptModel';
import { republishPromptModelWorkflowPrediction } from './operations/republishWorkflowPrediction';
import { resolvePromptModelTargetEntitiesDetails } from './operations/resolvePromptModelTargetEntitiesDetails';
import { resolvePromptModelTargetEntityDetails } from './operations/resolvePromptModelTargetEntityDetails';
import { saveLibraryModel } from './operations/saveLibraryModel';
import { savePromptModelDraft } from './operations/savePromptModelDraft';
import { saveTableModelDraft } from './operations/saveTableModelDraft';
import { suggestPromptModelInstructions } from './operations/suggestPromptModelInstructions';
import { suspendPromptModel } from './operations/suspendPromptModel';
import { tagAnswerForPromptModelTestCase } from './operations/tagAnswerForPromptModelTestCase';
import { updatePromptModelProgress } from './operations/updatePromptModelProgress';
import { updatePromptModelTestCase } from './operations/updatePromptModelTestCase';
import { sylvanusApiRoot } from './root';

sylvanusApiRoot.enhanceEndpoints({ endpoints: autogenEnhancements });

const libraryListTag = { type: Tags.library, id: 'LIST' } as const;
const modelResultListTag = { type: Tags.model, id: 'RESULT_LIST' } as const;
const modelSummaryListTag = { type: Tags.model, id: 'SUMMARY_LIST' } as const;

/**
 * @lintignore Not imported by name anywhere, but this export must exist: dropping it
 * (or knip's fix un-exporting/deleting it) risks the module never being evaluated, which
 * would mean `injectEndpoints` never registers these endpoints on the shared root api.
 */
export const sylvanusApi = sylvanusApiRoot.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    checkLibraryModelConfig: build.mutation({
      queryFn: createQueryFn(checkLibraryModelConfig),
    }),
    checkPromptModelConfig: build.mutation({
      queryFn: createQueryFn(checkPromptModelConfig),
    }),
    createPromptModelTestCase: build.mutation({
      queryFn: createQueryFn(createPromptModelTestCase),
    }),
    createPromptModelVersion: build.mutation({
      queryFn: createQueryFn(createPromptModelVersion),
    }),
    deletePromptModel: build.mutation({
      queryFn: createQueryFn(deletePromptModel),
      invalidatesTags: [
        libraryListTag,
        modelResultListTag,
        modelSummaryListTag,
      ],
    }),
    executePromptModelTestCases: build.mutation({
      queryFn: createQueryFn(executePromptModelTestCases),
    }),
    getPromptModelProgress: build.query({
      queryFn: createQueryFn(getPromptModelProgress),
      providesTags: (_result, _error, { modelId }) => [
        { type: Tags.model, id: `PROGRESS_${modelId}` },
      ],
    }),
    getPromptModelOptimizeResults: build.query({
      queryFn: createQueryFn(getPromptModelOptimizeResults),
    }),
    getPromptModelOptimizeTestCases: build.query({
      queryFn: createQueryFn(getPromptModelOptimizeTestCases),
    }),
    optimizePromptModel: build.mutation({
      queryFn: createQueryFn(optimizePromptModel),
      invalidatesTags: [modelResultListTag],
    }),
    getPromptModelOptimizeSummary: build.query({
      queryFn: createQueryFn(getPromptModelOptimizeSummary),
    }),
    publishPromptModel: build.mutation({
      queryFn: createQueryFn(publishPromptModel),
      invalidatesTags: (_result, _error, { modelId }) => [
        libraryListTag,
        modelResultListTag,
        { type: Tags.model, id: `PROGRESS_${modelId}` },
        modelSummaryListTag,
      ],
    }),
    suggestPromptModelInstructions: build.query({
      queryFn: createQueryFn(suggestPromptModelInstructions),
    }),
    suspendPromptModel: build.mutation({
      queryFn: createQueryFn(suspendPromptModel),
      invalidatesTags: (_result, _error, { modelId }) => [
        libraryListTag,
        modelResultListTag,
        { type: Tags.model, id: `PROGRESS_${modelId}` },
        modelSummaryListTag,
      ],
    }),
    tagAnswerForPromptModelTestCase: build.mutation({
      queryFn: createQueryFn(tagAnswerForPromptModelTestCase),
    }),
    updatePromptModelProgress: build.mutation({
      queryFn: createQueryFn(updatePromptModelProgress),
      invalidatesTags: (_result, _error, { modelId }) => [
        { type: Tags.model, id: `PROGRESS_${modelId}` },
      ],
    }),
    updatePromptModelTestCase: build.mutation({
      queryFn: createQueryFn(updatePromptModelTestCase),
    }),
    getModelSummary: build.query({
      queryFn: createQueryFn(getModelSummary),
      providesTags: [modelSummaryListTag],
    }),
    getModelFailedDocs: build.query({
      queryFn: createQueryFn(getModelFailedDocs),
    }),
    getModelsQuotaSummary: build.query({
      queryFn: createQueryFn(getModelsQuotaSummary),
    }),
    getDocsInScope: build.query({
      queryFn: createQueryFn(getDocsInScope),
    }),
    getModelResult: build.query({
      queryFn: createQueryFn(getModelResult),
      providesTags: (_result, _error, { modelId }) => [
        { type: Tags.model, id: modelId },
      ],
    }),
    getModelsByDocumentId: build.query({
      queryFn: createQueryFn(getModelsByDocumentId),
      keepUnusedDataFor: 600,
    }),
    getPromptModel: build.query({
      queryFn: createQueryFn(getPromptModel),
      providesTags: (_result, _error, { modelId }) => [
        { type: Tags.model, id: modelId },
      ],
    }),
    getPromptModelPublish: build.query({
      queryFn: createQueryFn(getPromptModelPublish),
    }),
    getPromptModelWorkflowPrediction: build.query({
      queryFn: createQueryFn(getPromptModelWorkflowPrediction),
    }),
    getPromptModelWorkflowPredictionByDocument: build.query({
      queryFn: createQueryFn(getPromptModelWorkflowPredictionByDocument),
    }),
    getPromptModelWorkflowPredictions: build.query({
      queryFn: createQueryFn(getPromptModelWorkflowPredictions),
      keepUnusedDataFor: 600,
    }),
    republishPromptModelWorkflowPrediction: build.mutation({
      queryFn: createQueryFn(republishPromptModelWorkflowPrediction),
    }),
    getPromptModelResults: build.query({
      queryFn: createQueryFn(getPromptModelResults),
      providesTags: [modelResultListTag],
    }),
    getPromptModelTestCases: build.query({
      queryFn: createQueryFn(getPromptModelTestCases),
    }),
    getPromptModelVersion: build.query({
      queryFn: createQueryFn(getPromptModelVersion),
      keepUnusedDataFor: 600,
    }),
    DEPRECATED_getPromptModelVersion: build.query({
      queryFn: createQueryFn(DEPRECATED_getPromptModelVersion),
    }),
    getPromptModelFieldIds: build.query({
      queryFn: createQueryFn(getPromptModelFieldIds),
      providesTags: [{ type: Tags.model, id: 'FIELD_IDS' }],
    }),
    getPromptModelTargetEntities: build.query({
      queryFn: createQueryFn(getPromptModelTargetEntities),
      providesTags: [{ type: Tags.model, id: 'TARGET_ENTITIES' }],
    }),
    getPublishedFieldPromptModelTargetEntities: build.query({
      queryFn: createQueryFn(getPublishedFieldPromptModelTargetEntities),
      providesTags: [
        { type: Tags.model, id: 'PUBLISHED_FIELD_TARGET_ENTITIES' },
      ],
    }),
    resolvePromptModelTargetEntitiesDetails: build.query({
      queryFn: createQueryFn(resolvePromptModelTargetEntitiesDetails),
    }),
    resolvePromptModelTargetEntityDetails: build.query({
      queryFn: createQueryFn(resolvePromptModelTargetEntityDetails),
    }),
    savePromptModelDraft: build.mutation({
      queryFn: createQueryFn(savePromptModelDraft),
      invalidatesTags: (result, _error, { modelId }) => [
        modelResultListTag,
        modelSummaryListTag,
        { type: Tags.model, id: 'FIELD_IDS' },
        ...(modelId || result?.id
          ? [{ type: Tags.model, id: modelId ?? result?.id }]
          : []),
      ],
    }),
    saveTableModelDraft: build.mutation({
      queryFn: createQueryFn(saveTableModelDraft),
      invalidatesTags: (result, _error, { modelId }) => [
        modelResultListTag,
        modelSummaryListTag,
        ...(modelId || result?.modelId
          ? [{ type: Tags.model, id: modelId ?? result?.modelId }]
          : []),
      ],
    }),
    adminRepublishModel: build.mutation({
      queryFn: createQueryFn(adminRepublishModel),
    }),
    deleteLibraryModel: build.mutation({
      queryFn: createQueryFn(deleteLibraryModel),
      invalidatesTags: (_result, _error, { libraryModelId }) => [
        libraryListTag,
        { type: Tags.library, id: libraryModelId },
      ],
    }),
    getLibraryModel: build.query({
      queryFn: createQueryFn(getLibraryModel),
      keepUnusedDataFor: 600,
      providesTags: (_result, _error, { libraryModelId }) => [
        { type: Tags.library, id: libraryModelId },
      ],
    }),
    getLibraryModels: build.query({
      queryFn: createQueryFn(getLibraryModels),
      keepUnusedDataFor: 600,
      providesTags: [libraryListTag],
    }),
    getLibraryModelsPublishStatus: build.query({
      queryFn: createQueryFn(getLibraryModelsPublishStatus),
      keepUnusedDataFor: 0,
    }),
    publishLibraryModels: build.mutation({
      queryFn: createQueryFn(publishLibraryModels),
      invalidatesTags: [
        libraryListTag,
        modelResultListTag,
        modelSummaryListTag,
      ],
    }),
    saveLibraryModel: build.mutation({
      queryFn: createQueryFn(saveLibraryModel),
      invalidatesTags: (_result, _error, { libraryModelId }) => [
        libraryListTag,
        ...(libraryModelId ? [{ type: Tags.library, id: libraryModelId }] : []),
      ],
    }),
  }),
});
